import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Search, Download, Mail, Edit, Trash2, FileCheck, AlertTriangle, RotateCcw, XCircle } from 'lucide-react';
import { formatDateShort } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const QuotesList = () => {
  const [quotes, setQuotes] = useState([]);
  const [trash, setTrash] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'trash' ? 'trash' : 'active';
  const setTab = (value) => setSearchParams(value === 'trash' ? { tab: 'trash' } : {});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dupeGroups, setDupeGroups] = useState([]);
  const mutationRef = useRef(new Set());
  const [busyIds, setBusyIds] = useState(new Set());
  const [actionError, setActionError] = useState('');
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchQuotes();
    fetchTrash();
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/quotes-duplicate-numbers`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setDupeGroups(data.duplicate_groups || []);
    } catch (err) {
      logger.error('Fetch duplicate quote numbers:', err);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes`, {
        cache: 'no-store',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        setActionError('Could not load the current quotes. Please try again.');
      }
    } catch (err) {
      logger.error('Failed to fetch quotes:', err);
      setActionError('Could not load quotes. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrash = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/quotes/trash`, {
        cache: 'no-store',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) setTrash(await res.json());
    } catch (err) {
      logger.error('Failed to fetch trashed quotes:', err);
    }
  };

  const changeQuoteState = async (quoteId, action) => {
    if (mutationRef.current.has(quoteId)) return;
    if (action === 'delete' && !window.confirm('Move this quote to trash? You must restore it before editing it again.')) return;
    mutationRef.current.add(quoteId);
    setBusyIds(new Set(mutationRef.current));
    setActionError('');
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}${action === 'restore' ? '/restore' : ''}`, {
        method: action === 'restore' ? 'POST' : 'DELETE',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        if (action === 'delete') setQuotes((current) => current.filter((q) => q.id !== quoteId));
        else setTrash((current) => current.filter((q) => q.id !== quoteId));
        await Promise.all([fetchQuotes(), fetchTrash()]);
      } else {
        const data = await response.json().catch(() => ({}));
        setActionError(data.detail || 'Could not update this quote. Please try again.');
      }
    } catch (err) {
      logger.error('Failed to change quote state:', err);
      setActionError('Could not update this quote. Check your connection and try again.');
    } finally {
      mutationRef.current.delete(quoteId);
      setBusyIds(new Set(mutationRef.current));
    }
  };

  const handleDelete = (quoteId) => changeQuoteState(quoteId, 'delete');

  const handleRestore = async (quoteId) => {
    await changeQuoteState(quoteId, 'restore');
  };

  const handlePermanentDelete = async (quoteId) => {
    if (!window.confirm('Permanently delete this quote? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (response.ok) fetchTrash();
    } catch (err) {
      logger.error('Failed to permanently delete quote:', err);
    }
  };

  const handleDownloadPDF = async (quote) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quote.id}/download-pdf`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Quote_${quote.client_name.replace(/\s/g, '_')}_${quote.quote_number.replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (err) {
      logger.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      accepted: 'bg-green-100 text-green-800 border-green-200',
      declined: 'bg-red-100 text-red-800 border-red-200',
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const activeList = tab === 'trash' ? trash : quotes;
  const filteredQuotes = activeList.filter(quote => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = (
      (quote.client_name || '').toLowerCase().includes(q) ||
      (quote.client_email || '').toLowerCase().includes(q) ||
      (quote.quote_number || '').toLowerCase().includes(q) ||
      (quote.origin || '').toLowerCase().includes(q) ||
      (quote.destination || '').toLowerCase().includes(q)
    );
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading quotes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-primary">Quotes</h1>
              <p className="text-sm text-foreground/60">Manage all quotes</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/dashboard')}
                data-testid="quotes-dashboard-button"
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Dashboard
              </button>
              <button
                onClick={() => navigate('/admin/quotes/new')}
                data-testid="quotes-new-button"
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Quote
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b border-border -mb-px">
            <button
              onClick={() => setTab('active')}
              data-testid="tab-active-quotes"
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'active' ? 'border-primary text-primary' : 'border-transparent text-foreground/60 hover:text-foreground'}`}
            >
              Active <span className="ml-1 text-xs text-foreground/50">({quotes.length})</span>
            </button>
            <button
              onClick={() => setTab('trash')}
              data-testid="tab-trash-quotes"
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${tab === 'trash' ? 'border-primary text-primary' : 'border-transparent text-foreground/60 hover:text-foreground'}`}
            >
              <Trash2 className="w-3.5 h-3.5" /> Trash <span className="text-xs text-foreground/50">({trash.length})</span>
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" />
              <input
                data-testid="quotes-search"
                type="text"
                placeholder="Search by client name or quote number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              data-testid="quotes-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted</option>
              <option value="paid">Paid</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="container mx-auto px-6 py-8">
        {actionError && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800" data-testid="quotes-action-error">
          {actionError}
          <button type="button" className="ml-3 underline" data-testid="quotes-retry" onClick={() => { setActionError(''); fetchQuotes(); fetchTrash(); }}>Retry</button>
        </div>}
        {dupeGroups.length > 0 && (
          <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="dupe-quote-numbers-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-amber-900">
                  {dupeGroups.length} quote number{dupeGroups.length === 1 ? ' has' : 's have'} been used more than once
                </h3>
                <p className="text-xs text-amber-800 mt-0.5">
                  Legacy data — the auto-numbering now prevents this. Click a quote below to open it directly.
                </p>
                <ul className="mt-3 space-y-2">
                  {dupeGroups.map((g) => (
                    <li key={g.quote_number} className="text-sm" data-testid={`dupe-group-${g.quote_number}`}>
                      <span className="font-mono font-semibold text-amber-900">{g.quote_number}</span>
                      <span className="text-amber-800"> · {g.count} quotes share this number:</span>
                      <ul className="ml-4 mt-1 space-y-1">
                        {g.quotes.map((q) => (
                          <li key={q.id}>
                            <button
                              onClick={() => navigate(`/admin/quotes/${q.id}`)}
                              data-testid={`dupe-open-${q.id}`}
                              className="text-primary hover:underline text-left"
                            >
                              → {q.client_name || <em className="text-foreground/60">(no client)</em>}
                              {q.client_email && <span className="text-foreground/60"> · {q.client_email}</span>}
                              <span className="text-foreground/60"> · {q.status || 'draft'}</span>
                              {q.total != null && <span className="text-foreground/60"> · {q.currency || 'CI$'}{Number(q.total).toFixed(2)}</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          {filteredQuotes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-foreground/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No quotes found</h3>
              <p className="text-foreground/60 mb-4">Create your first quote to get started</p>
              <button
                onClick={() => navigate('/admin/quotes/new')}
                data-testid="quotes-empty-create-button"
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Quote
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Quote #</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Route</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id} data-testid={`quote-row-${quote.id}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span data-testid={`quote-number-${quote.id}`} className="font-mono text-sm font-semibold text-primary">{quote.quote_number}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-foreground">
                            {quote.client_name || <span className="text-foreground/40 italic">(no client — draft)</span>}
                          </div>
                          <div className="text-sm text-foreground/60">{quote.client_email || '—'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-foreground">{quote.origin || '—'}</div>
                          <div className="text-foreground/60">→ {quote.destination || '—'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground/70">
                        {formatDateShort(quote.quote_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span data-testid={`quote-total-${quote.id}`} className="font-semibold text-foreground">{quote.currency || 'CI$'}{Number(quote.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(quote.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {tab === 'trash' ? (
                            <>
                              <button
                                onClick={() => navigate(`/admin/quotes/${quote.id}`)}
                                className="p-2 text-foreground/60 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                title="View"
                                data-testid={`view-trashed-quote-${quote.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRestore(quote.id)}
                                disabled={busyIds.has(quote.id)}
                                data-testid={`restore-quote-${quote.id}`}
                                className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                                title="Restore this quote"
                              >
                                <RotateCcw className="w-3.5 h-3.5" /> Restore
                              </button>
                              <button
                                onClick={() => handlePermanentDelete(quote.id)}
                                data-testid={`permadelete-quote-${quote.id}`}
                                className="p-2 text-foreground/60 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete permanently"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => navigate(`/admin/quotes/${quote.id}`)}
                                className="p-2 text-foreground/60 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                title="View"
                                data-testid={`view-quote-${quote.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadPDF(quote)}
                                className="p-2 text-foreground/60 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Download PDF"
                                data-testid={`download-quote-${quote.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => navigate(`/admin/quotes/${quote.id}/edit`)}
                                className="p-2 text-foreground/60 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                                data-testid={`edit-quote-${quote.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {!quote.converted_to_invoice && (
                                <button
                                  onClick={() => navigate(`/admin/quotes/${quote.id}`)}
                                  className="p-2 text-foreground/60 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                  title="Convert to Invoice"
                                  data-testid={`convert-quote-${quote.id}`}
                                >
                                  <FileCheck className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(quote.id)}
                                disabled={busyIds.has(quote.id)}
                                data-testid={`delete-quote-${quote.id}`}
                                className="p-2 text-foreground/60 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Move to trash"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
